import { useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import httpMethod from 'services/update2025/httpMethod';

import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";

import { SubjectContext } from "components/subject/SubjectContext";
import { InputTextList } from "components/InputTextList";
import { createSubject, updateSubject, validateSubject } from "services/SubjectService";
import { handleError } from "utils/handleError";

export const SubjectEditor = () => {
    const toast = useRef(null);
    const { t, i18n } = useTranslation(["subject", "common"]);
    const tokenObj = httpMethod.getTokenStorage(); // { access_token, … }
    const accessToken = tokenObj?.access_token;;

    let headers = {
        Authorization: `Bearer ${accessToken}`,
        "Accept-Language": i18n.language,
    };

    const subjectContext = useContext(SubjectContext);
    const { subject, resetSubject, setSubject, subjects, setSubjects, submitted, setSubmitted, subjectDialog, setSubjectDialog } = subjectContext;

    const onInputChange = (e, name) => {
        const val = e.target?.value || "";
        let _subject = { ...subject };
        _subject[`${name}`] = val;
        setSubject(_subject);
    };

    const onEnabledChange = (e) => {
        let _subject = { ...subject };
        _subject["enabled"] = e.checked;
        setSubject(_subject);
    };

    const onActionsChange = (list) => {
        let _subject = { ...subject, actions: list };
        setSubject(_subject);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setSubjectDialog(false);
    };

    const saveSubjectAction = () => {
        setSubmitted(true);

        validateSubject(subject)
            .then(() => {
                if (!subject.modifiedDate) {
                    // Creating
                    return createSubject(headers, subject).then((res) => {
                        let _subjects = [...subjects];
                        _subjects.push(res.data);
                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.created", { ns: "common", object: t("subject") }), life: 3000 });

                        setSubjects(_subjects);
                        setSubjectDialog(false);
                        resetSubject();
                    });
                } else {
                    // Updating
                    return updateSubject(headers, subject.code, {
                        name: subject.name,
                        enabled: subject.enabled,
                        actions: subject.actions.filter((s) => s),
                        description: subject.description,
                    }).then((res) => {
                        let _subjects = [...subjects];
                        const index = findIndexByCode(subject.code);
                        _subjects[index] = res.data;

                        toast.current.show({ severity: "success", summary: t("success", { ns: "common" }), detail: t("inform.updated", { ns: "common", object: t("subject") }), life: 3000 });

                        setSubjects(_subjects);
                        setSubjectDialog(false);
                        resetSubject();
                    });
                }
            })
            .catch((error) => handleError(toast, t, error));
    };

    const findIndexByCode = (code) => {
        let index = -1;
        for (let i = 0; i < subjects.length; i++) {
            if (subjects[i].code === code) {
                index = i;
                break;
            }
        }

        return index;
    };

    const dialogFooter = (
        <>
            <Button label={t("cancel", { ns: "common" })} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={t("save", { ns: "common" })} icon="pi pi-check" onClick={saveSubjectAction} />
        </>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog visible={subjectDialog} style={{ width: "450px" }} header={t("details", { ns: "common" })} modal onHide={hideDialog} className="p-fluid text-center">
                <div className="formgrid">
                    <div className="grid text-left">
                        <div className="col-12">
                            <div className="field">
                                <label htmlFor="code">{t("code")}</label>
                                <InputText id="code" value={subject.code} keyfilter={/[^\s]/} maxLength={50} onChange={(e) => onInputChange(e, "code")} required autoFocus readOnly={subject.modifiedDate} className={classNames({ "p-invalid": submitted && !subject.code })} />
                                {submitted && !subject.code && <small className="p-invalid">{t("code.required")}.</small>}
                            </div>
                            <div className="field">
                                <label htmlFor="name">{t("name")}</label>
                                <InputText id="name" value={subject.name} maxLength={100} onChange={(e) => onInputChange(e, "name")} required className={classNames({ "p-invalid": submitted && !subject.name })} />
                                {submitted && !subject.name && <small className="p-invalid">{t("name.required")}.</small>}
                            </div>
                            <div className="field flex flex-column">
                                <label htmlFor="enabled">{t("enabled")}</label>
                                <Checkbox id="enabled" checked={subject.enabled} onChange={onEnabledChange} />
                            </div>
                            <div className="field flex flex-column">
                                <label htmlFor="actions">{t("action", { ns: "common" })}</label>
                                <InputTextList id="actions" value={subject.actions?.length > 0 ? subject.actions : [""]} onChange={(items) => onActionsChange(items)} />
                            </div>
                            <div className="field">
                                <label htmlFor="description">{t("description")}</label>
                                <InputTextarea id="description" value={subject.description || ""} maxLength={500} onChange={(e) => onInputChange(e, "description")} rows={3} cols={20} />
                            </div>
                        </div>
                    </div>
                    <div className="grid">
                        <div className="col-12">
                            {/* save */}
                            <Button
                                label={t("save", { ns: "common" })}
                                style={{
                                    width: "max-content",
                                }}
                                className="mr-2"
                                icon="pi pi-check"
                                onClick={saveSubjectAction}
                            />
                            {/* close */}
                            <Button
                                label={t("cancel", { ns: "common" })}
                                icon="pi pi-times"
                                className="mr-2 p-button-danger"
                                style={{
                                    width: "max-content",
                                }}
                                onClick={hideDialog}
                            />
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
